import type { DateTime } from "luxon";
import type { HospitalData } from "../../../utils/models";

const DAYS_PER_WEEK = 7;

/** A loaded week that can be exported, carrying its own position in `hospitalData.weeks`. */
export type ExportableWeek = {
	/** Index into `hospitalData.weeks` — what `StaffingPDF` renders from. */
	weekIndex: number;
	/** The Sunday that opens the week. */
	start: DateTime;
};

/**
 * The weeks the export can offer, in order.
 *
 * A week's start date comes from its first shift row's first day, and nothing in the types
 * guarantees either exists — a week carrying no shift rows has no date to name, so it is skipped
 * instead of throwing on `[0].days[0]` and blanking the popup.
 *
 * The index travels with the date precisely so that skipping is safe: those indices are what the PDF
 * renders from, so dropping an entry from a plain positional array would silently shift every later
 * week and export the wrong ones.
 */
export const exportableWeeks = (hospitalData: HospitalData): ExportableWeek[] =>
	(hospitalData.weeks ?? []).reduce<ExportableWeek[]>((weeks, week, weekIndex) => {
		const start = week.weeklyShifts.at(0)?.days.at(0)?.date;
		if (start) weeks.push({ weekIndex, start });
		return weeks;
	}, []);

/** Indices into `hospitalData.weeks` for every exportable week starting inside [start, end]. */
export const weekIndicesInRange = (weeks: ExportableWeek[], start: DateTime | undefined, end: DateTime | undefined): number[] => {
	if (!start || !end) return [];
	const from = start.startOf("day");
	const to = end.startOf("day");
	return weeks.reduce<number[]>((indices, week) => {
		const day = week.start.startOf("day");
		if (day >= from && day <= to) indices.push(week.weekIndex);
		return indices;
	}, []);
};

/**
 * The widest a pick may land from the week boundary it means and still be attributed to it.
 *
 * A pick can arrive shifted by up to a day: the picker produces a date in the BROWSER's zone and
 * `DateInput` converts it to the department's, which moves the calendar day whenever the two zones
 * straddle midnight (browsing from UTC+3 against a UTC-7 facility turns Sunday into Saturday). Since
 * the pickers only enable real boundaries, and boundaries sit a week apart, anything within a few
 * days of one unambiguously identifies it — while exact matching silently drops the pick entirely.
 *
 * Two days: the widest offset between any two real zones is 26 hours, so this absorbs every possible
 * shift while still rejecting a date that names no loaded week.
 */
const PICK_TOLERANCE_DAYS = 2;

/** Calendar date in the value's OWN zone — the day a user reading it would name. */
const calendarDate = (date: DateTime): string => date.toFormat("yyyy-MM-dd");

/**
 * Resolve a picked date to the start of the exportable week it identifies.
 *
 * Matches the NEAREST week boundary rather than an exact one, so a cross-timezone pick can't be
 * dropped (see `PICK_TOLERANCE_DAYS`). The end picker hands back a week's LAST day, which has to
 * resolve to the same week as its first.
 */
export const weekStartOf = (weeks: ExportableWeek[], picked: DateTime, edge: "start" | "end"): DateTime | undefined => {
	const target = edge === "end" ? picked.minus({ days: DAYS_PER_WEEK - 1 }) : picked;
	let nearest: DateTime | undefined;
	let nearestDistance = Number.POSITIVE_INFINITY;
	for (const { start } of weeks) {
		const distance = Math.abs(start.diff(target, "days").days);
		if (distance < nearestDistance) {
			nearestDistance = distance;
			nearest = start;
		}
	}
	return nearestDistance <= PICK_TOLERANCE_DAYS ? nearest : undefined;
};

/**
 * True when `date` opens an exportable week — the start picker's enabled dates.
 *
 * Compares calendar dates, not instants: `shouldDisableDate` receives each day in the picker's own
 * frame, so the honest question is "does the day this cell SHOWS name a loaded week start", and
 * `DateTime.equals` would answer no for an equivalent day carrying a different zone or locale.
 */
export const isAvailableWeekStart = (weeks: ExportableWeek[], date: DateTime): boolean => {
	const day = calendarDate(date);
	return weeks.some((week) => calendarDate(week.start) === day);
};

/** True when `date` closes an exportable week — the end picker's enabled dates. */
export const isAvailableWeekEnd = (weeks: ExportableWeek[], date: DateTime): boolean => isAvailableWeekStart(weeks, date.minus({ days: DAYS_PER_WEEK - 1 }));
